/**
 * Vue i18n internationalization utilities
 * Provides Vue i18n instance configuration and type definitions for translations
 * Configured with French as primary locale and English as fallback
 *
 * @public
 * @useWhen Need translations; Support multiple languages
 */

import messages from 'src/i18n/index'
import { createI18n } from 'vue-i18n'

/**
 * Type definition for supported message languages
 * Extracted from the messages object keys
 *
 * @public
 */
export type MessageLanguages = keyof typeof messages

/**
 * Type definition for the message schema
 * Uses 'en' as the master schema for TypeScript type inference
 *
 * @public
 */
export type MessageSchema = (typeof messages)['en']

// See https://vue-i18n.intlify.dev/guide/advanced/typescript.html#global-resource-schema-type-definition

declare module 'vue-i18n' {
  // define the locale messages schema
  export interface DefineLocaleMessage extends MessageSchema {}

  // define the datetime format schema
  export interface DefineDateTimeFormat {}

  // define the number format schema
  export interface DefineNumberFormat {}
}

/**
 * Raw Vue i18n instance for advanced usage
 * Provides direct access to the i18n instance for programmatic translation access
 * Use this when you need the full i18n instance rather than just the global API
 *
 * @public
 * @example
 * ```typescript
 * import { i18nInstance } from 'src/CrudManager/Core/Utils/i18n'
 * const translated = i18nInstance.global.t('my.key')
 * ```
 */
export const i18nInstance = createI18n({
  legacy: false,
  locale: 'fr',
  fallbackLocale: 'en',
  messages,
})

/**
 * Vue i18n global API for translations
 * Provides convenient access to translation functions
 * This is the recommended way to access translations in most cases
 *
 * @public
 * @example
 * ```typescript
 * import { i18n } from 'src/CrudManager/Core/Utils/i18n'
 * const message = i18n.t('common.save')
 * ```
 */
export const i18n = i18nInstance.global
